import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  cyTag?: string;
}

export const Pagination = ({ currentPage, totalPages, onPageChange, cyTag }: PaginationProps) => {
  const getPageButtons = () => {
    const pages: number[] = [];

    if (currentPage === 1) {
      // First page: show 1, 2, 3
      for (let i = 1; i <= Math.min(3, totalPages); i++) {
        pages.push(i);
      }
    } else if (currentPage === totalPages) {
      // Last page: show last 3 pages
      for (let i = Math.max(1, totalPages - 2); i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Middle pages: show previous, current, next
      pages.push(currentPage - 1, currentPage, currentPage + 1);
    }

    return pages;
  };

  const pageButtons = getPageButtons();

  const BUTTON_STYLES = classNames(
    'relative inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-sm font-medium',
    'border border-grey-800',
    'transition-colors duration-150',
    'focus:z-20 focus:outline-none focus:ring-2 focus:ring-primary-50/20'
  );

  const ACTIVE_BUTTON_STYLES = classNames(
    'bg-primary-50 text-white border-primary-50 cursor-default'
  );
  const INACTIVE_BUTTON_STYLES = classNames(
    'text-grey-200 bg-white hover:bg-primary-t-900 hover:text-primary-50 hover:border-primary-t-700 cursor-pointer'
  );

  return (
    <nav
      aria-label="Pagination"
      className="flex items-center justify-center gap-1.5 pt-5 pb-2"
      data-cy={cyTag}
    >
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        data-cy={cyTag ? `${cyTag}-previous` : 'pagination-previous'}
        className={classNames(
          BUTTON_STYLES,
          currentPage === 1
            ? 'text-grey-600 border-grey-900 cursor-not-allowed'
            : INACTIVE_BUTTON_STYLES
        )}
        aria-label="Previous page"
      >
        <ChevronLeftIcon aria-hidden="true" className="size-5" />
      </button>

      {pageButtons.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          disabled={page === currentPage}
          data-cy={cyTag ? `${cyTag}-page-${page}` : `pagination-page-${page}`}
          className={classNames(
            BUTTON_STYLES,
            page === currentPage ? ACTIVE_BUTTON_STYLES : INACTIVE_BUTTON_STYLES
          )}
          aria-label={`Page ${page}`}
          aria-current={page === currentPage ? 'page' : undefined}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        data-cy={cyTag ? `${cyTag}-next` : 'pagination-next'}
        className={classNames(
          BUTTON_STYLES,
          currentPage === totalPages
            ? 'text-grey-600 border-grey-900 cursor-not-allowed'
            : INACTIVE_BUTTON_STYLES
        )}
        aria-label="Next page"
      >
        <ChevronRightIcon aria-hidden="true" className="size-5" />
      </button>
    </nav>
  );
};
